import { ScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { Score, ScoreVariation } from "src/types";
import { getActionPlanScoreIds, isDisplayedAsMainScore, isScoreEligibleForScoreType } from "src/utils/scores";

export type ScoreTypeSelection = Required<
    Pick<ScoreType, "scoreIds" | "subscoreIds" | "variationIds" | "defaultVariationIds">
> & { variationOrder: number[]; actionPlanScoreIds: number[] };

type ScoreWithVariations = Pick<Score, "id" | "variations">;

export const SCORES_DISPLAYED_ENTITY_TYPES = ["company", "product", "ingredient", "packaging", "transport"] as const;
export type ScoresDisplayedEntityType = (typeof SCORES_DISPLAYED_ENTITY_TYPES)[number];

export const AVAILABLE_SCORES_PREVIEW_COUNT = 6;

export const getScoreTypeSelection = (scoreType: ScoreType | undefined): ScoreTypeSelection => ({
    scoreIds: scoreType?.scoreIds ?? [],
    subscoreIds: scoreType?.subscoreIds ?? [],
    variationIds: scoreType?.variationIds ?? [],
    defaultVariationIds: scoreType?.defaultVariationIds ?? [],
    variationOrder: scoreType?.variationOrder ?? [],
    actionPlanScoreIds: getActionPlanScoreIds(scoreType),
});

const variationIdsOf = (score: ScoreWithVariations): number[] =>
    score.variations.map((variation: ScoreVariation) => variation.id);

const withoutVariationsOf = (selection: ScoreTypeSelection, scores: ScoreWithVariations[]): ScoreTypeSelection => {
    const droppedVariationIds = scores.flatMap(variationIdsOf);

    return {
        ...selection,
        variationIds: selection.variationIds.filter((id) => !droppedVariationIds.includes(id)),
        defaultVariationIds: selection.defaultVariationIds.filter((id) => !droppedVariationIds.includes(id)),
        variationOrder: selection.variationOrder.filter((id) => !droppedVariationIds.includes(id)),
    };
};

export const toggleScore = (selection: ScoreTypeSelection, score: ScoreWithVariations): ScoreTypeSelection => {
    if (!selection.scoreIds.includes(score.id)) {
        return {
            ...selection,
            scoreIds: [...selection.scoreIds, score.id],
            actionPlanScoreIds: [...selection.actionPlanScoreIds.filter((id) => id !== score.id), score.id],
        };
    }

    return withoutVariationsOf(
        {
            ...selection,
            scoreIds: selection.scoreIds.filter((id) => id !== score.id),
            actionPlanScoreIds: selection.actionPlanScoreIds.filter((id) => id !== score.id),
        },
        [score],
    );
};

export const setSubscoreSelection = (
    selection: ScoreTypeSelection,
    parent: Pick<Score, "children">,
    selectedSubscoreIds: number[],
): ScoreTypeSelection => {
    const subscores = parent.children ?? [];
    const subscoreIds = subscores.map((subscore) => subscore.id);
    const deselectedSubscores = subscores.filter(
        (subscore) => selection.subscoreIds.includes(subscore.id) && !selectedSubscoreIds.includes(subscore.id),
    );
    const keptSubscoreIds = selection.subscoreIds.filter(
        (id) => !subscoreIds.includes(id) || selectedSubscoreIds.includes(id),
    );
    const addedSubscoreIds = subscoreIds.filter(
        (id) => selectedSubscoreIds.includes(id) && !selection.subscoreIds.includes(id),
    );

    return withoutVariationsOf(
        { ...selection, subscoreIds: [...keptSubscoreIds, ...addedSubscoreIds] },
        deselectedSubscores,
    );
};

export const toggleVariation = (selection: ScoreTypeSelection, variationId: number): ScoreTypeSelection => {
    if (!selection.variationIds.includes(variationId)) {
        return { ...selection, variationIds: [...selection.variationIds, variationId] };
    }

    return {
        ...selection,
        variationIds: selection.variationIds.filter((id) => id !== variationId),
        defaultVariationIds: selection.defaultVariationIds.filter((id) => id !== variationId),
    };
};

export const selectDefaultVariation = (
    selection: ScoreTypeSelection,
    score: ScoreWithVariations,
    variationId: number | null,
): ScoreTypeSelection => {
    const scoreVariationIds = variationIdsOf(score);
    const otherDefaults = selection.defaultVariationIds.filter((id) => !scoreVariationIds.includes(id));

    return {
        ...selection,
        defaultVariationIds: variationId === null ? otherDefaults : [...otherDefaults, variationId],
    };
};

export const toggleActionPlanScore = (selection: ScoreTypeSelection, scoreId: number): ScoreTypeSelection => ({
    ...selection,
    actionPlanScoreIds:
        selection.actionPlanScoreIds.includes(scoreId) ?
            selection.actionPlanScoreIds.filter((id) => id !== scoreId)
        :   [...selection.actionPlanScoreIds, scoreId],
});

export const hasCustomVariationOrder = (selection: ScoreTypeSelection, score: ScoreWithVariations): boolean =>
    variationIdsOf(score).some((id) => selection.variationOrder.includes(id));

export const setScoreVariationOrder = (
    selection: ScoreTypeSelection,
    score: ScoreWithVariations,
    orderedVariationIds: number[],
): ScoreTypeSelection => {
    const scoreVariationIds = variationIdsOf(score);

    return {
        ...selection,
        variationOrder: [
            ...selection.variationOrder.filter((id) => !scoreVariationIds.includes(id)),
            ...orderedVariationIds,
        ],
    };
};

export const resetScoreVariationOrder = (
    selection: ScoreTypeSelection,
    score: ScoreWithVariations,
): ScoreTypeSelection => setScoreVariationOrder(selection, score, []);

export const applyDisplayedOrder = (scoreIds: number[], orderedDisplayedIds: number[]): number[] => {
    const remaining = [...orderedDisplayedIds];

    return scoreIds.map((id) => (orderedDisplayedIds.includes(id) ? (remaining.shift() ?? id) : id));
};

export const isListedAsMainScore = (score: Score): boolean =>
    isDisplayedAsMainScore(score) || score.parent === undefined || score.parent === null;

export const getDisplayedScores = (scoreIds: number[], scores: Score[]): Score[] =>
    scoreIds
        .map((id) => scores.find((score) => score.id === id))
        .filter((score): score is Score => score !== undefined && isListedAsMainScore(score));

export const getAvailableScores = (scoreIds: number[], scores: Score[], type: ScoreType["type"]): Score[] =>
    scores.filter(
        (score) =>
            isListedAsMainScore(score) && !scoreIds.includes(score.id) && isScoreEligibleForScoreType(score, type),
    );

export const filterScoresByTitle = (scores: Score[], query: string): Score[] => {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery === "") return scores;

    return scores.filter((score) => score.title.toLowerCase().includes(normalizedQuery));
};

export type ScoreSettingsSummary = {
    enabledVariationCount: number;
    variationCount: number;
    enabledSubscoreCount: number;
    subscoreCount: number;
};

export const getScoreSettingsSummary = (score: Score, selection: ScoreTypeSelection): ScoreSettingsSummary => {
    const subscores = score.children ?? [];

    return {
        enabledVariationCount: score.variations.filter((variation) => selection.variationIds.includes(variation.id))
            .length,
        variationCount: score.variations.length,
        enabledSubscoreCount: subscores.filter((subscore) => selection.subscoreIds.includes(subscore.id)).length,
        subscoreCount: subscores.length,
    };
};
